import { useState, useEffect } from "react";
import { createFocusBlock } from "../api";
import { todayISO } from "../lib/planner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Target, Loader2 } from "lucide-react";

const DURATIONS = [25, 50, 90];

export const FocusBlockDialog = ({ open, onOpenChange, tasks, onCreated }) => {
  const candidates = [
    ...(tasks?.top3 || []),
    ...(tasks?.fits_today || []),
  ].filter((t) => !t.completed);

  const [taskId, setTaskId] = useState("");
  const [duration, setDuration] = useState(25);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && candidates.length) setTaskId(candidates[0].id);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = candidates.find((t) => t.id === taskId);

  const create = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const res = await createFocusBlock({
        task_id: selected.id,
        content: selected.content,
        duration_min: Number(duration),
        date: todayISO(),
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      const where = [
        res.calendar_created && "Google Calendar",
        res.todoist_updated && "Todoist",
      ].filter(Boolean);
      if (where.length) {
        const hint = res.calendar_connected && !res.can_write ? " · reconnect Calendar in Settings to add it there too" : "";
        toast.success(`Focus block ${res.start}–${res.end} added to ${where.join(" & ")} 🌿${hint}`);
      } else if (res.calendar_connected && !res.can_write) {
        toast.info(`Blocked ${res.start}–${res.end}. Reconnect Google Calendar in Settings to also add it there.`);
      } else {
        toast.success(`Focus block set for ${res.start}–${res.end}`);
      }
      onOpenChange(false);
      onCreated && onCreated();
    } catch {
      toast.error("Couldn't create the focus block");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="focus-block-dialog" className="rounded-3xl max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Target className="h-5 w-5 text-[#b1543b]" /> Protect focus time
          </DialogTitle>
          <DialogDescription>
            Ebbly finds the first free slot in your day and blocks it in your calendar & Todoist.
          </DialogDescription>
        </DialogHeader>

        {candidates.length === 0 ? (
          <p className="text-sm text-stone-400 py-4">No open tasks to protect right now 🌱</p>
        ) : (
          <div className="space-y-5 pt-2">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-stone-400 mb-2">Task</p>
              <div className="space-y-1.5 max-h-52 overflow-y-auto">
                {candidates.map((t) => (
                  <button
                    key={t.id}
                    data-testid={`focus-pick-${t.id}`}
                    onClick={() => setTaskId(t.id)}
                    className={`w-full text-left px-3 py-2.5 rounded-2xl border transition-colors ${
                      taskId === t.id
                        ? "border-[#b1543b] bg-[#e7d2c4] dark:bg-stone-800"
                        : "border-stone-100 dark:border-stone-800 hover:border-stone-200"
                    }`}
                  >
                    <span className="text-sm text-[#3b2a24] dark:text-stone-100">{t.content}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-stone-400 mb-2">Duration</p>
              <div className="flex items-center gap-2">
                {DURATIONS.map((d) => (
                  <button
                    key={d}
                    data-testid={`focus-duration-${d}`}
                    onClick={() => setDuration(d)}
                    className={`flex-1 h-11 rounded-2xl text-sm font-semibold border transition-colors ${
                      Number(duration) === d
                        ? "bg-[#b1543b] text-white border-[#b1543b]"
                        : "border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-300"
                    }`}
                  >
                    {d}m
                  </button>
                ))}
                <Input
                  data-testid="focus-duration-custom"
                  type="number"
                  min={5}
                  step={5}
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="w-20 h-11 rounded-2xl text-center border-stone-200 dark:border-stone-700"
                />
              </div>
            </div>

            <Button
              data-testid="focus-block-create"
              onClick={create}
              disabled={busy || !selected}
              className="w-full h-12 rounded-full bg-[#b1543b] hover:bg-[#8f3f2b] text-white font-semibold"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Find my free slot & block it"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
